'use client';

import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { Star, Check, Plus } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useWatchlist } from '@/hooks/useWatchlist';
import { cn } from '@/lib/utils';

interface WatchButtonProps {
  ticker: string;
  /** 'icon'  = compact star-only button hidden until parent has `group`
   *            hover (used on watchlist row tiles).
   *  'pill'  = star-only button visible at all times, sized + bordered
   *            to match the stock-detail header action row. Previously
   *            rendered with an "Add to Watchlist" / "Watching" text
   *            label — dropped per the detail-page header cleanup so
   *            the ticker name + symbol badge dominate the row. */
  variant?: 'icon' | 'pill';
  /** When true, the icon variant ignores the group-hover gate and stays
   *  visible at rest. Use on action columns (e.g. Scanner Log row) where
   *  the star sits next to other always-visible buttons (chart/flask) and
   *  hover-reveal would feel inconsistent. Has no effect on the pill
   *  variant (already always-visible). */
  alwaysVisible?: boolean;
  className?: string;
}

export default function WatchButton({ ticker, variant = 'icon', alwaysVisible = false, className }: WatchButtonProps) {
  const { user } = useAuth();
  const { lists, mounted, isWatched, isInList, toggleInList } = useWatchlist();
  const [open, setOpen] = useState(false);
  const [flash, setFlash] = useState(false);
  const [flipUp, setFlipUp] = useState(false);
  const [alignLeft, setAlignLeft] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Flip the dropdown above the trigger when there isn't enough room
  // below — last few rows of a screener page were clipping the menu
  // against the pagination footer. Estimated height = 40px header + ~36px
  // per list row; flip if the menu would exceed the visible viewport.
  useLayoutEffect(() => {
    if (!open || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const estimatedHeight = 40 + 36 * Math.max(1, lists.length);
    const spaceBelow = window.innerHeight - rect.bottom;
    setFlipUp(estimatedHeight > spaceBelow && rect.top > estimatedHeight);
    // The menu is right-aligned (right-0) and MENU_W wide, so it extends to
    // the LEFT of the trigger. When the star sits near the left edge — e.g.
    // the mobile stock-detail toolbar — that pushes the menu off the left of
    // the viewport and clips the list names. Flip to left-aligned when the
    // right-aligned menu would overflow the left edge AND left-aligning fits.
    const MENU_W = 192; // w-48
    const MARGIN = 8;
    const overflowsLeft = rect.right - MENU_W < MARGIN;
    const leftAlignFits = rect.left + MENU_W <= window.innerWidth - MARGIN;
    setAlignLeft(overflowsLeft && leftAlignFits);
  }, [open, lists.length]);

  // Not logged in or not mounted — render nothing
  if (!user || !mounted) return null;

  const watched = isWatched(ticker);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (lists.length === 1) {
      // Single list — toggle immediately
      toggleInList(lists[0].id, ticker);
      setFlash(true);
      setTimeout(() => setFlash(false), 600);
    } else {
      // Multiple lists — show picker
      setOpen((o) => !o);
    }
  };

  const handleListToggle = (e: React.MouseEvent, listId: string) => {
    e.preventDefault();
    e.stopPropagation();
    toggleInList(listId, ticker);
    setFlash(true);
    setTimeout(() => setFlash(false), 600);
  };

  if (variant === 'pill') {
    return (
      <div ref={ref} className={cn('relative', className)}>
        <button
          onClick={handleClick}
          title={watched ? 'Remove from watchlist' : 'Add to watchlist'}
          aria-label={watched ? 'Remove from watchlist' : 'Add to watchlist'}
          className={cn(
            'flex items-center justify-center w-9 h-9 rounded-lg border transition-all',
            watched
              ? 'bg-amber-500/10 border-amber-500/30 text-amber-400 hover:bg-amber-500/20'
              : 'bg-card-bg border-default text-fg-muted hover:text-white hover:border-muted'
          )}
        >
          <Star
            size={15}
            className={cn('transition-all', watched ? 'fill-amber-400 text-amber-400' : '', flash && 'scale-125')}
          />
        </button>
        {open && <ListDropdown lists={lists} ticker={ticker} isInList={isInList} onToggle={handleListToggle} onClose={() => setOpen(false)} flipUp={flipUp} alignLeft={alignLeft} />}
      </div>
    );
  }

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        onClick={handleClick}
        title={watched ? 'Remove from watchlist' : 'Add to watchlist'}
        className={cn(
          'p-1.5 rounded-md transition-all',
          // Hover-reveal by default (compact watchlist rows), but
          // alwaysVisible callers (action columns alongside other
          // always-shown icons) skip that gate.
          alwaysVisible
            ? 'opacity-100'
            : 'opacity-0 group-hover:opacity-100 focus:opacity-100',
          watched
            ? 'text-amber-400 opacity-100'
            : 'text-slate-600 hover:text-amber-400 hover:bg-amber-400/10'
        )}
      >
        <Star
          size={14}
          className={cn('transition-all', watched && 'fill-amber-400', flash && 'scale-125')}
        />
      </button>
      {open && <ListDropdown lists={lists} ticker={ticker} isInList={isInList} onToggle={handleListToggle} onClose={() => setOpen(false)} flipUp={flipUp} alignLeft={alignLeft} />}
    </div>
  );
}

function ListDropdown({
  lists,
  ticker,
  isInList,
  onToggle,
  onClose,
  flipUp,
  alignLeft,
}: {
  lists: ReturnType<typeof useWatchlist>['lists'];
  ticker: string;
  isInList: (listId: string, ticker: string) => boolean;
  onToggle: (e: React.MouseEvent, listId: string) => void;
  onClose: () => void;
  flipUp: boolean;
  alignLeft: boolean;
}) {
  return (
    <div className={cn(
      'absolute w-48 bg-card-bg border border-default rounded-xl shadow-2xl z-50 py-1.5 overflow-hidden',
      alignLeft ? 'left-0' : 'right-0',
      flipUp ? 'bottom-full mb-1' : 'top-full mt-1',
    )}>
      <div className="px-3 py-1.5 text-[10px] font-semibold text-fg-subtle uppercase tracking-wider border-b border-default mb-1">
        Add to list
      </div>
      {lists.map((list) => {
        const inList = isInList(list.id, ticker);
        return (
          <button
            key={list.id}
            onClick={(e) => { onToggle(e, list.id); onClose(); }}
            className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-card-hover-bg transition-colors text-left"
          >
            <span className={cn('truncate', inList ? 'text-amber-400' : 'text-slate-300')}>{list.name}</span>
            {inList
              ? <Check size={13} className="text-amber-400 flex-shrink-0" />
              : <Plus size={13} className="text-slate-600 flex-shrink-0" />
            }
          </button>
        );
      })}
    </div>
  );
}
